import { FC } from "react";
import BarChart from "../components/BarChart/BarChart";
import EmptyState from "../components/EmptyState/EmptyState";
import useTrackingAggregate from "../hooks/useTrackingAggregate";
import useTrackingFunnel from "../hooks/useTrackingFunnel";
import { EventNames } from "../utils/eventNames";
import { type Period } from "../utils/period";

interface Props {
	period: Period;
	availableEvents: Set<string>;
}

const OperationsTab: FC<Props> = ({ period, availableEvents }) => {
	const steps = [
		EventNames.FORM_OPENED,
		EventNames.FORM_STARTED,
		EventNames.FORM_SUBMITTED
	];
	const formAvailable = steps.every((s) => availableEvents.has(s));

	const funnel = useTrackingFunnel(
		{
			steps,
			from: period.from,
			to: period.to,
			withinSeconds: 1800
		},
		{ enabled: formAvailable }
	);

	const submittedAvail = availableEvents.has(EventNames.FORM_SUBMITTED);
	const byRegion = useTrackingAggregate(
		{
			event: EventNames.FORM_SUBMITTED,
			from: period.from,
			to: period.to,
			groupBy: "orgTeamLevelOne"
		},
		{ enabled: submittedAvail }
	);

	return (
		<div className="space-y-8">
			<div className="rounded-xl border border-border bg-white shadow-sm p-4">
				<h3 className="font-heading text-base font-medium mb-3">
					Form completion funnel (30-min window)
				</h3>
				{!formAvailable ? (
					<EmptyState
						title="Form events incomplete"
						description={`Required: ${steps.join(" → ")}.`}
						tone="warn"
					/>
				) : funnel.isLoading ? (
					<div className="h-32 animate-pulse bg-zinc-100 rounded" />
				) : (
					<BarChart
						ariaLabel="Form completion funnel"
						data={(funnel.data?.steps ?? []).map((s) => ({
							label: `${s.index + 1}. ${s.step} (${(
								s.conversion_from_start * 100
							).toFixed(0)}%)`,
							value: s.users_reached
						}))}
						valueLabel="users"
					/>
				)}
			</div>

			<div className="rounded-xl border border-border bg-white shadow-sm p-4">
				<h3 className="font-heading text-base font-medium mb-3">
					Submissions by region
				</h3>
				{!submittedAvail ? (
					<EmptyState
						title="No data yet"
						description="FORM_SUBMITTED isn't being emitted."
					/>
				) : byRegion.isLoading ? (
					<div className="h-32 animate-pulse bg-zinc-100 rounded" />
				) : (
					<BarChart
						ariaLabel="Form submissions by region"
						data={(byRegion.data?.buckets ?? []).map((b) => ({
							label: b.bucket,
							value: b.count
						}))}
						valueLabel="submissions"
					/>
				)}
			</div>
		</div>
	);
};

export default OperationsTab;
